import { createContext, useContext, useEffect, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";

export type IconLibrary = "lucide" | "tabler" | "phosphor" | "remix" | "fontawesome" | "reicon" | "animateicons";

export type DawTheme = "default";

export const THEME_OPTIONS: Array<{ id: DawTheme; label: string }> = [
  { id: "default", label: "Default" },
];

export interface AppearanceOption {
  id: string;
  label: string;
  value: string;
}

export const FONT_OPTIONS: AppearanceOption[] = [
  { id: "system", label: "System Default", value: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  { id: "inter", label: "Inter", value: "'Inter', sans-serif" },
  { id: "geist", label: "Geist", value: "'Geist', 'Inter', sans-serif" },
  { id: "manrope", label: "Manrope", value: "'Manrope', 'Inter', sans-serif" },
  { id: "dm-sans", label: "DM Sans", value: "'DM Sans', 'Inter', sans-serif" },
  { id: "ibm-plex", label: "IBM Plex Sans", value: "'IBM Plex Sans', 'Inter', sans-serif" },
  { id: "source-sans", label: "Source Sans 3", value: "'Source Sans 3', 'Inter', sans-serif" },
  { id: "roboto", label: "Roboto", value: "'Roboto', 'Inter', sans-serif" },
  { id: "open-sans", label: "Open Sans", value: "'Open Sans', 'Inter', sans-serif" },
  { id: "jetbrains", label: "JetBrains Mono", value: "'JetBrains Mono', monospace" },
  { id: "fira-code", label: "Fira Code", value: "'Fira Code', 'JetBrains Mono', monospace" },
  { id: "monospace", label: "System Mono", value: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" },
];

export const ICON_LIBRARY_OPTIONS: Array<{ id: IconLibrary; label: string }> = [
  { id: "lucide", label: "Lucide" },
  { id: "tabler", label: "Tabler" },
  { id: "phosphor", label: "Phosphor" },
  { id: "remix", label: "Remix" },
  { id: "fontawesome", label: "Font Awesome" },
  { id: "reicon", label: "ReIcon" },
  { id: "animateicons", label: "AnimateIcons" },
];

const FONT_KEY = "johnsonboibeats-mini-daw-font";
const ICON_KEY = "johnsonboibeats-mini-daw-icon-library";
const THEME_KEY = "johnso…e.v3";

interface AppearanceContextValue {
  fontId: string;
  fontFamily: string;
  iconLibrary: IconLibrary;
  theme: DawTheme;
  resetAppearance: () => void;
  setFontId: Dispatch<SetStateAction<string>>;
  setIconLibrary: Dispatch<SetStateAction<IconLibrary>>;
  setTheme: Dispatch<SetStateAction<DawTheme>>;
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

function readFont(): string {
  try {
    const saved = localStorage.getItem(FONT_KEY);
    return FONT_OPTIONS.some((option) => option.id === saved) ? saved! : "dm-sans";
  } catch {
    return "dm-sans";
  }
}

function readIconLibrary(): IconLibrary {
  try {
    const saved = localStorage.getItem(ICON_KEY) as IconLibrary | null;
    return ICON_LIBRARY_OPTIONS.some((option) => option.id === saved) ? saved! : "lucide";
  } catch {
    return "lucide";
  }
}

function readTheme(): DawTheme {
  try {
    const saved = localStorage.getItem(THEME_KEY) as DawTheme | null;
    return THEME_OPTIONS.some((option) => option.id === saved) ? saved! : "default";
  } catch {
    return "default";
  }
}

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [fontId, setFontId] = useState(readFont);
  const [iconLibrary, setIconLibrary] = useState<IconLibrary>(readIconLibrary);
  const [theme, setTheme] = useState<DawTheme>(readTheme);

  useEffect(() => {
    const font = FONT_OPTIONS.find((option) => option.id === fontId) ?? FONT_OPTIONS[4];
    try { localStorage.setItem(FONT_KEY, font.id); } catch { /* Browser storage may be disabled. */ }
  }, [fontId]);

  useEffect(() => {
    try { localStorage.setItem(ICON_KEY, iconLibrary); } catch { /* Browser storage may be disabled. */ }
  }, [iconLibrary]);

  useEffect(() => {
    try { localStorage.setItem(THEME_KEY, theme); } catch { /* Browser storage may be disabled. */ }
  }, [theme]);

  const value = useMemo<AppearanceContextValue>(() => ({
    fontId,
    fontFamily: (FONT_OPTIONS.find((option) => option.id === fontId) ?? FONT_OPTIONS[4]).value,
    iconLibrary,
    theme,
    resetAppearance: () => {
      setFontId("dm-sans");
      setIconLibrary("lucide");
      setTheme("default");
    },
    setFontId,
    setIconLibrary,
    setTheme,
  }), [fontId, iconLibrary, theme]);

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export function useAppearance(): AppearanceContextValue {
  const context = useContext(AppearanceContext);
  if (!context) throw new Error("useAppearance must be used inside AppearanceProvider");
  return context;
}
